import type { ReactNode } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import { withRouter } from 'react-router-dom';

import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import { useProjectStore } from '@core/store';
import classy from '@core/utils/classy';
import Icon from '@ui/Icon';
import RDMD from '@ui/RDMD';
import SmartLink from '@ui/SmartLink';

import classes from './index.module.scss';

let suggestionsPreloaded = false;
const preloadSuggestionsRouter = () => {
  if (!suggestionsPreloaded && typeof window !== 'undefined') {
    suggestionsPreloaded = true;
    import('@routes/Suggestions');
  }
};

interface ContainerProps {
  children: ReactNode;
  githubLink?: string | null;
  slug?: string;
  superHub?: boolean;
}

const WithSuggestEditsLink = ({ slug, superHub = false, children, githubLink }: ContainerProps) => {
  const bem = useClassy(classes, 'SuggestEdit');

  const LinkProps = superHub
    ? { href: githubLink || '', rel: 'noreferrer', target: '_blank' }
    : { to: `/edit/${slug}`, rel: 'nofollow' };

  const linkText = `Suggest Edit${superHub ? '' : 's'}`;
  const shouldPreload = !superHub;

  return (
    <div className="row clearfix">
      <div className="col-xs-9">{children}</div>
      <div className="col-xs-3">
        <SmartLink
          {...LinkProps}
          className={classy(bem(superHub && ''), !superHub && 'suggestEdits', 'rm-SuggestEdit')}
          onFocus={shouldPreload ? preloadSuggestionsRouter : undefined}
          onMouseEnter={shouldPreload ? preloadSuggestionsRouter : undefined}
        >
          {superHub ? (
            <Icon
              aria-hidden="true"
              className={classy(bem('-icon', '-icon_git'), 'rm-SuggestEdit-icon')}
              name="github-filled"
            />
          ) : (
            <i aria-hidden="true" className="icon icon-edit" />
          )}
          {linkText}
          {!!superHub && (
            <Icon
              aria-label="Opens in a new tab"
              className={classy(bem('-icon', '-icon_arrow'), 'rm-SuggestEdit-icon')}
              name="arrow-up-right"
            />
          )}
        </SmartLink>
      </div>
    </div>
  );
};

const NoLinks = ({ children }: ContainerProps) => (
  <div className="row clearfix">
    <div className="col-xs-9">{children}</div>
  </div>
);

interface MatchParams {
  path?: string;
}

interface HeaderProps extends RouteComponentProps<MatchParams> {
  breadcrumb?: ReactNode;
  excerpt: string;
  githubLink?: string | null;
  lang: string;
  slugUrl?: string;
  suggestedEdits: boolean;
  superHub?: boolean;
  title?: string;
}

const Header = ({
  breadcrumb,
  excerpt,
  githubLink,
  lang,
  match,
  slugUrl,
  suggestedEdits,
  title,
  superHub = false,
}: HeaderProps) => {
  let Container = NoLinks;

  const [git, isRmdx] = useProjectStore(s => [s.data.git, s.data.features.mdx === 'enabled']);

  const isConnectedToPublicRepo = git.connection?.repository?.privacy.visibility === 'public';
  const isLanguageSupported = lang === 'en';
  const isSuggestedEditsRoute = match?.path?.endsWith('/edit') ?? false;

  /** Indicates whether we should show the "Suggest Edits" link */
  const showSuggestEditsLink = superHub
    ? suggestedEdits && isLanguageSupported && !isSuggestedEditsRoute && isConnectedToPublicRepo
    : suggestedEdits && isLanguageSupported && !isSuggestedEditsRoute;

  if (showSuggestEditsLink) {
    Container = superHub ? WithSuggestEditsLink : WithSuggestEditsLink;
  }

  const rdmdOpts = useRdmdOpts();

  return (
    <header id="content-head">
      <Container githubLink={githubLink} slug={slugUrl} superHub={superHub}>
        {!!breadcrumb && breadcrumb}
        <h1>{title}</h1>
        {!!excerpt && (
          <div className="excerpt">
            <RDMD opts={rdmdOpts} renderer={isRmdx ? 'rmdx' : 'rdmd'}>
              {excerpt}
            </RDMD>
          </div>
        )}
      </Container>
    </header>
  );
};

export default withRouter(Header);
